'use client'

import { useMemo } from 'react'
import { NewsFavicon } from '@/components/NewsFavicon'
import { ReliabilityDots } from '@/components/ui/SourceMeta'
import Tooltip from '@/components/tooltip'
import { RelativeTime } from '@/utils/date'
import { RiLock2Line } from 'react-icons/ri'
import SideModal from '@/components/modals/SideModal'
import { useTopicContext } from './TopicContext'
import { getBiasCategory, getBiasLabel, getReliabilityInfo } from '@/utils/bias'

const BIAS_FILTERS = [
  { value: 'left', label: 'L', tooltip: 'Left-leaning outlets', borderColor: 'border-l-blue-500', activeBg: 'bg-blue-50 dark:bg-blue-950' },
  { value: 'center', label: 'C', tooltip: 'Center outlets', borderColor: 'border-l-gray-400', activeBg: 'bg-gray-50 dark:bg-gray-900' },
  { value: 'right', label: 'R', tooltip: 'Right-leaning outlets', borderColor: 'border-l-red-500', activeBg: 'bg-red-50 dark:bg-red-950' },
]

export default function ArticlesModal() {
  const {
    mobileModalOpen,
    mobileModalArticles,
    mobileModalTitle,
    closeMobileModal,
    biasFilters,
    toggleBias,
  } = useTopicContext()

  const filteredArticles = useMemo(() => {
    return mobileModalArticles.filter(article => {
      const bias = article.outlet?.bias ?? 0
      return biasFilters.includes(getBiasCategory(bias))
    })
  }, [mobileModalArticles, biasFilters])

  return (
    <SideModal
      isOpen={mobileModalOpen}
      setIsOpen={closeMobileModal}
      title={mobileModalTitle}
      maxWidth="lg"
    >
      {/* Filters - sticky at top */}
      <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-3 bg-card border-b border-border">
        <div className="flex gap-0.5">
          {BIAS_FILTERS.map(filter => {
            const isActive = biasFilters.includes(filter.value)
            return (
              <button
                key={filter.value}
                onClick={() => toggleBias(filter.value)}
                className={`w-8 h-7 text-xs font-bold border-l-[3px] rounded-r transition-colors ${
                  isActive
                    ? `${filter.borderColor} ${filter.activeBg} text-foreground`
                    : 'border-l-transparent bg-muted text-muted-foreground'
                }`}
              >
                {filter.label}
              </button>
            )
          })}
        </div>

        <span className="text-sm text-muted-foreground ml-auto">
          {filteredArticles.length} of {mobileModalArticles.length}
        </span>
      </div>

      {/* Article list */}
      <div className="p-4 space-y-2">
        {filteredArticles.map((article) => {
          const biasLabel = getBiasLabel(article.outlet?.bias ?? 0)
          const reliabilityInfo = getReliabilityInfo(article.outlet?.reliability ?? 0)
          return (
            <a
              key={article.id}
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group block p-3 rounded-sm border border-border hover:bg-muted/30 transition-all"
            >
              <div className="flex items-start gap-3">
                <NewsFavicon
                  src={`${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/public_assets/news-favicons/${article.outlet?.slug}.jpg`}
                  alt={article.outlet?.name}
                  faviconSize="sm"
                  className="flex-shrink-0 mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground leading-snug line-clamp-2 group-hover:text-accent transition-colors">
                    {article.title}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <span className="text-xs text-muted-foreground">
                      {article.outlet?.name}
                    </span>
                    <span className={`text-xs font-medium ${biasLabel.color}`}>
                      {biasLabel.label}
                    </span>
                    <Tooltip message={reliabilityInfo.description}>
                      <span className="flex items-center gap-1">
                        <ReliabilityDots reliability={article.outlet?.reliability} size="sm" />
                      </span>
                    </Tooltip>
                    {article.outlet?.is_paywalled && (
                      <Tooltip message="This outlet requires a subscription to read full articles.">
                        <span><RiLock2Line className="w-3 h-3 text-muted-foreground" /></span>
                      </Tooltip>
                    )}
                    <span className="text-xs text-muted-foreground ml-auto">
                      <RelativeTime date={article.published_date} />
                    </span>
                  </div>
                </div>
              </div>
            </a>
          )
        })}

        {filteredArticles.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            No articles match filters
          </p>
        )}
      </div>
    </SideModal>
  )
}
